import {
  createContext,
  type FC,
  type PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useUITheme } from '@crehana/compass.ui';
import { useTranslation } from '@crehana/i18n';
import { BucketsEnum, useUploadFile } from '@crehana/react-hooks';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import type { SnackbarProps } from '@crehana/ui';
import { useRouter } from 'next/router';

import useConfiguration from '@/views/AdminDashboard/hooks/useConfiguration';

import type {
  TErrorCodes,
  TInvalidRecords,
} from '../components/Step2/hooks/useDataValidation/types';
import type {
  IExcelImporterStepperProps,
  IExcelImporterStepperState,
  TFileProgress,
  TNextStepHandler,
  TOnValidatedHandler,
  TS3ReadyFile,
  TShouldShowHanlder,
  TValidationsMetaData,
} from './types';

const DEFAULT_MAX_UPLOAD_SIZE_KB = 500;

export const ExcelImporterStepperContext = createContext<
  IExcelImporterStepperState | undefined
>(undefined);

const ExcelImporterStepperProvider: FC<
  PropsWithChildren<IExcelImporterStepperProps>
> = ({
  children,
  panelUrl,
  onGenerateAttachmentUrl,
  isContentOnly,
  validations,
  onUpload,
  isUpdateTeams,
  continueOnError,
  maxFileSizeKb = DEFAULT_MAX_UPLOAD_SIZE_KB,
  centralized,
  setFileIdCourse,
  ...rest
}) => {
  const { t } = useTranslation();
  const { isDark } = useUITheme();
  const { originalId } = useConfiguration();
  const { query, push } = useRouter();
  const { slug } = query as { slug: string };
  const [isNewUpload, setIsNewUpload] = useState(false);

  const [steps, setSteps] = useState(3);
  const [currentStep, setCurrentStep] = useState(1);
  const { uploadFile, uploading } = useUploadFile(originalId);
  const [uploadProgress, setUploadProgress] = useState<TFileProgress>();
  const [file, setFile] = useState<File | undefined>();
  const [fileNameUrl, setFileNameUrl] = useState('');

  const [filePathUrl, setFilePathUrl] = useState('');
  const [succesfulImportId, setSuccesfulImportId] = useState<
    string | undefined
  >();
  const [errors, setErrors] = useState<TInvalidRecords[] | undefined>();
  const [validationMetadata, setValidationMetadata] =
    useState<TValidationsMetaData>();
  const [isNextButtonActive, setIsNextButtonActive] = useState(false);
  const [validationStepVisited, setVisitedValidationStep] = useState(false);
  const [currentErrorRevision, setCurrentErrorRevision] = useState<
    TErrorCodes | undefined
  >();
  const [snackbar, setSnackbar] = useState<SnackbarProps | 'defaultError'>({});

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (currentStep === 1) {
      setIsNextButtonActive(Boolean(file));
      setFileNameUrl('');
      setFilePathUrl('');
      setErrors(undefined);
      setValidationMetadata(undefined);
      setCurrentErrorRevision(undefined);
      setVisitedValidationStep(false);
    }
  }, [currentStep]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (currentStep === 1 && file) setIsNextButtonActive(true);
    if (currentStep === 2) {
      if (validationStepVisited) {
        if (continueOnError) {
          setIsNextButtonActive(
            Boolean(
              validationMetadata?.totalValidRecords &&
                validationMetadata.totalValidRecords > 0,
            ),
          );
        } else {
          setIsNextButtonActive(
            !validationMetadata?.code || validationMetadata?.code === 0,
          );
        }
      } else {
        setIsNextButtonActive(false);
      }
    }
  }, [file, currentStep, validationStepVisited]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (snackbar === 'defaultError') {
      setSnackbar({
        open: true,
        state: 'alert',
        message: t('SNACKBAR_DEFAULT_ERROR'),
      });
    }
  }, [snackbar]);

  const handleNextStep: TNextStepHandler = codes => {
    if (currentStep === 2) {
      if (codes) {
        setCurrentErrorRevision(codes);
      } else {
        centralized &&
          succesfulImportId &&
          setCurrentStep(prevState => prevState + 1);
        !centralized && handleSubmit().then(() => setCurrentStep(3));
      }
    } else {
      setCurrentStep(prevState => prevState + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep === 1) {
      return push(panelUrl);
    } else if (currentStep === 2 && currentErrorRevision) {
      return setCurrentErrorRevision(undefined);
    } else if (currentStep === 3) {
      return setCurrentStep(1);
    } else {
      return setCurrentStep(currentStep - 1);
    }
  };

  const handleSetStepperInSpecificStep = (step: number) => {
    setCurrentStep(step);
  };

  const handleOnChangeFile = (file: File, isNewMethod?: boolean) => {
    const size = file.size / 1024;
    if (size > maxFileSizeKb) {
      setSnackbar({
        open: true,
        state: 'alert',
        message: t('SNACKBAR_FILE_SIZE_ERROR'),
      });
      return;
    }
    if (isNewMethod) {
      uploadFileToAws(file);
      return;
    }
    uploadAttachment(file);
  };

  const uploadFileToAws = async (file: File) => {
    try {
      setIsNextButtonActive(false);

      const response = await uploadFile({
        file: file,
        options: {
          bucket: BucketsEnum.PublicLesson,
          content_type: file.type,
          filename: file.name,
        },
        onUploadProgress: setUploadProgress,
      });

      if (!response.success) {
        throw new Error('Upload failed');
      }
      setFile(file);
      setFilePathUrl(response.url || '');
      setFileNameUrl(file.name || '');
      if (setFileIdCourse) {
        setFileIdCourse(response.fileId);
      }

      setIsNextButtonActive(true);
    } catch (error) {
      setSnackbar({
        open: true,
        state: 'alert',
      });
      setIsNextButtonActive(false);
    } finally {
      setUploadProgress(undefined);
    }
  };

  const uploadAttachment = (file: File) => {
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = async () => {
      try {
        const response = await onGenerateAttachmentUrl(file);
        if (response) {
          await uploadFileToS3Bucket(response);
          // biome-ignore lint/style/useBlockStatements: single-line early return
        } else setSnackbar('defaultError');
      } catch (err) {
        setSnackbar('defaultError');
      }
    };
  };

  const uploadFileToS3Bucket = async ({
    payload,
    url,
    file,
    filename,
  }: TS3ReadyFile) => {
    if (file && url && payload.length > 0 && filename) {
      const formData = new FormData();

      payload.forEach(field => {
        if (field.fieldName && field.fieldValue) {
          formData.append(field.fieldName, field.fieldValue);
        }
      });

      formData.append('file', file);

      try {
        const response = await fetch(url, {
          method: 'POST',
          body: formData,
        });

        if (response.status === 204) {
          setFile(file);
          setFilePathUrl(url || '');
          setFileNameUrl(filename || '');
          // biome-ignore lint/style/useBlockStatements: single-line early return
        } else setSnackbar('defaultError');
      } catch (err) {
        setSnackbar('defaultError');
      }
    }
  };

  const handleSubmit = async () => {
    const fileUpload = centralized
      ? `${filePathUrl}/${fileNameUrl}`
      : validationMetadata?.validatedFileUpload || fileNameUrl;
    try {
      const success = await onUpload({
        fileupload: fileUpload,
        isUpdate: isUpdateTeams,
        errors,
      });
      // biome-ignore lint/style/useBlockStatements: single-line early return
      if (!success) setSnackbar('defaultError');
      // biome-ignore lint/style/useBlockStatements: single-line early return
      if (typeof success === 'string') setSuccesfulImportId(success);

      return !!success;
    } catch {
      setSnackbar('defaultError');
      return false;
    }
  };

  const shouldShow: TShouldShowHanlder = steps => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (!isContentOnly) return true;
    return !!isContentOnly.some(step => !steps.includes(step));
  };

  const handleSetValidationResults: TOnValidatedHandler = (
    metadata,
    errors,
  ) => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (metadata) setValidationMetadata(metadata);
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (errors) setErrors(errors);
  };

  const hasNestedValidations = useMemo(() => {
    return validations.some(validation => validation.subCodes);
  }, [validations]);

  const value = {
    slug,
    isDark,
    panelUrl,
    isUpdateTeams,

    validations,
    setVisitedValidationStep,
    validationStepVisited,
    hasNestedValidations,

    totalSteps: steps,
    handleSetSteps: (totalSteps: number) => setSteps(totalSteps),

    currentStep,
    handleNextStep,
    handlePreviousStep,

    file,
    setFile,
    fileNameUrl,
    maxFileSizeKb,
    filePathUrl,
    handleOnChangeFile,
    succesfulImportId,

    isNextButtonActive,
    setIsNextButtonActive,

    snackbar,
    setSnackbar,

    shouldShow,
    isContentOnly,

    setIsNewUpload,
    isNewUpload,

    onValidated: handleSetValidationResults,
    validationMetadata,
    errors,
    continueOnError,
    centralized,
    currentErrorRevision,

    onSubmit: handleSubmit,
    handleSetStepperInSpecificStep,
    uploadProgress,
    isLoadingProgress: uploading,
    ...rest,
  };

  return (
    <ExcelImporterStepperContext.Provider value={value}>
      {children}
    </ExcelImporterStepperContext.Provider>
  );
};

export default ExcelImporterStepperProvider;
